"use client"

import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  type VirtualElement,
} from "@floating-ui/dom"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import type { ComponentHierarchyItem } from "./types"
import { getDisplayPath, getWorkspaceRoot } from "./utils"

interface ComponentDropdownProps {
  x: number
  y: number
  hierarchy: ComponentHierarchyItem[]
  referenceElement: HTMLElement | null
  onItemClick: (item: ComponentHierarchyItem) => void
  onClose: () => void
}

/**
 * Dropdown component that displays the component hierarchy
 */
export function ComponentDropdown({
  x,
  y,
  hierarchy,
  referenceElement,
  onItemClick,
  onClose,
}: ComponentDropdownProps) {
  const [position, setPosition] = useState({ x, y })
  const [showAll, setShowAll] = useState(false)
  const [copied, setCopied] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const workspaceRoot = getWorkspaceRoot()
  const isWorkspaceRootMissing = !workspaceRoot

  const filteredHierarchy = showAll
    ? hierarchy
    : hierarchy.filter(
        (item) => item.fullPath !== null || item.type === "native"
      )

  useEffect(() => {
    const dropdownElement = dropdownRef.current
    if (!dropdownElement) return

    const updatePosition = async () => {
      // Ensure referenceElement is still in the DOM
      if (referenceElement && !document.contains(referenceElement)) {
        return
      }

      // Create a reference that always gets fresh bounding rect
      const reference: Element | VirtualElement = referenceElement
        ? {
            getBoundingClientRect: () => {
              // Always get fresh bounding rect from the actual element
              return referenceElement.getBoundingClientRect()
            },
          }
        : {
            getBoundingClientRect: () => ({
              x,
              y,
              width: 0,
              height: 0,
              top: y,
              left: x,
              right: x,
              bottom: y,
            }),
          }

      try {
        const { x: computedX, y: computedY } = await computePosition(
          reference,
          dropdownElement,
          {
            placement: "bottom-start",
            strategy: "fixed",
            middleware: [
              offset(10),
              flip({
                fallbackPlacements: ["top-start", "bottom-end", "top-end"],
              }),
              shift({
                padding: 10,
                crossAxis: true,
              }),
            ],
          }
        )

        setPosition({ x: computedX, y: computedY })
      } catch (_error) {
        // Fallback to original position if Floating UI fails
        setPosition({ x, y })
      }
    }

    // Initial position calculation
    updatePosition()

    // Use autoUpdate if we have a real DOM element, otherwise manually update
    if (referenceElement) {
      const cleanup = autoUpdate(
        referenceElement,
        dropdownElement,
        updatePosition,
        {
          ancestorScroll: true,
          ancestorResize: true,
          elementResize: true,
          layoutShift: true,
          animationFrame: false,
        }
      )
      return cleanup
    } else {
      // For virtual reference, manually update on scroll and resize
      window.addEventListener("scroll", updatePosition, true)
      window.addEventListener("resize", updatePosition)

      return () => {
        window.removeEventListener("scroll", updatePosition, true)
        window.removeEventListener("resize", updatePosition)
      }
    }
  }, [x, y, referenceElement])

  useEffect(() => {
    const dropdownElement = dropdownRef.current
    if (!dropdownElement) {
      return
    }

    // If a modal or dropdown is open it will set inert on all elements so we
    // need to manually remove it
    requestAnimationFrame(() => {
      dropdownElement.inert = false
    })

    return () => {
      requestAnimationFrame(() => {
        dropdownElement.inert = true
      })
    }
  }, [])

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] bg-transparent"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose()
          }
        }}
      />
      <div
        ref={dropdownRef}
        data-click-to-component-dropdown
        className="fixed bg-white border border-solid border-gray-200 rounded-[10px] shadow-sm max-w-[400px] max-h-[300px] overflow-auto text-xs"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          zIndex: 10000000,
        }}
      >
        <div className="px-2.5 py-1.5 border-b border-solid border-gray-200 flex items-center justify-between gap-2">
          <div className="font-semibold text-gray-900 text-[11px] uppercase tracking-[0.3px]">
            {isWorkspaceRootMissing
              ? "Setup Click to component"
              : "Component Hierarchy"}
          </div>
          {!isWorkspaceRootMissing && (
            <label
              className="flex items-center gap-1 cursor-pointer text-[11px] text-gray-500 select-none"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={showAll}
                onChange={(e) => {
                  e.stopPropagation()
                  setShowAll(e.target.checked)
                }}
                className="cursor-pointer m-0"
              />
              <span>Show all</span>
            </label>
          )}
        </div>
        <div className="py-0.5">
          {isWorkspaceRootMissing ? (
            <div className="p-3 text-gray-700 text-[11px] leading-relaxed">
              <div className="font-semibold mb-2 text-red-600">
                Workspace root not configured
              </div>
              <div className="mb-2">
                Add this to your{" "}
                <code className="font-mono text-[10px] bg-gray-100 px-1 py-0.5 rounded">
                  .env.local
                </code>{" "}
                file:
              </div>
              <div
                onClick={(e) => {
                  e.stopPropagation()
                  const text =
                    'NEXT_PUBLIC_CLICK_TO_COMPONENT_WORKSPACE_ROOT="/Users/sambernhardt/Dev/tt-analytics"'
                  navigator.clipboard.writeText(text).then(() => {
                    setCopied(true)
                    setTimeout(() => setCopied(false), 2000)
                  })
                }}
                className="font-mono text-[10px] bg-gray-50 border border-solid border-gray-200 rounded p-2 cursor-pointer select-none relative transition-colors duration-150 hover:bg-gray-100"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="break-all">
                    NEXT_PUBLIC_CLICK_TO_COMPONENT_WORKSPACE_ROOT=&quot;/Users/sambernhardt/Dev/tt-analytics&quot;
                  </span>
                  <span
                    className={`text-[9px] whitespace-nowrap font-medium ${
                      copied ? "text-green-500" : "text-gray-500"
                    }`}
                  >
                    {copied ? "✓ Copied" : "Click to copy"}
                  </span>
                </div>
              </div>
              <div className="mt-2 text-[10px] text-gray-500 italic">
                Update the path to match your workspace root directory
              </div>
            </div>
          ) : filteredHierarchy.length === 0 ? (
            <div className="p-3 text-center text-gray-400 text-[11px]">
              No components with file paths found
            </div>
          ) : (
            filteredHierarchy.map((item, index) => (
              <div
                key={index}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (item.fullPath) {
                    onItemClick(item)
                  }
                }}
                className={`py-1 px-2.5 border-l-2 border-solid border-transparent transition-all duration-150 ${
                  item.fullPath
                    ? "cursor-pointer bg-transparent hover:bg-gray-100 hover:border-l-[#007acc]"
                    : "cursor-default bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <span
                    className={`font-semibold text-xs ${
                      item.fullPath ? "text-[#007acc]" : "text-gray-500"
                    }`}
                  >
                    {item.name}
                  </span>
                  <span className="text-[10px] text-gray-400 lowercase">
                    {item.type}
                  </span>
                </div>
                {item.fullPath && (
                  <div className="text-[10px] text-gray-500 mt-0.5 font-mono break-all leading-[1.3]">
                    {getDisplayPath(item.fullPath)}
                  </div>
                )}
                {!item.fullPath && item.type === "native" && (
                  <div className="text-[10px] text-gray-400 mt-0.5 italic">
                    HTML element
                  </div>
                )}
                {!item.fullPath && item.type !== "native" && (
                  <div className="text-[10px] text-gray-400 mt-0.5 italic">
                    No file path available
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </>,
    document.body
  )
}
